import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    Award,
    BadgeCheck,
    BarChart3,
    ChevronsRight,
    Clapperboard,
    Drama,
    Film,
    Globe,
    Handshake,
    Landmark,
    Lightbulb,
    Map,
    Megaphone,
    Rocket,
    Star,
    Target,
    Trophy,
    Users,
} from "lucide-react";
import { AWARDS, VISION, MEDIA, PDF_BG } from "../data/content";
import SectionBg from "./SectionBg";

const AWARD_ICONS = [Star, Clapperboard, Film, Megaphone, Users, Trophy, Rocket, Globe, Users, BadgeCheck];
const VISION_ICONS = [Globe, BarChart3, Users, Handshake, Lightbulb, Drama, Globe, Target];
const JOURNEY_ICONS = [Landmark, Map, Globe];

export function Awards() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [-100, 100]);
    const rotate = useTransform(scrollYProgress, [0, 1], [-5, 5]);

    return (
        <section
            id="awards"
            ref={ref}
            className="relative py-24 md:py-40 overflow-hidden"
            data-testid="awards-section"
        >
            <SectionBg src={PDF_BG.awards} opacity={0.6} />
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                <div className="grid grid-cols-12 gap-6 md:gap-12 items-start">
                    <div className="col-span-12 lg:col-span-5 min-w-0">
                        <div className="text-xs uppercase tracking-[0.3em] text-[#FF6B35] font-mono mb-6">
                            The JAF Awards
                        </div>
                        <h2 className="min-w-0 max-w-full font-display font-bold text-[1.62rem] leading-[1.14] tracking-normal break-words [overflow-wrap:anywhere] sm:text-5xl sm:leading-[1.05] sm:tracking-tight lg:text-6xl mb-6 md:mb-8">
                            The night the industry <span className="gradient-text">crowns its own</span>.
                        </h2>
                        <p className="text-zinc-300 leading-relaxed text-sm sm:text-base md:text-lg mb-8 md:mb-10 [overflow-wrap:anywhere]">
                            A black-tie red-carpet evening celebrating the best work, the boldest agencies, the
                            creators who built tomorrow, and the icons who built it all.
                        </p>

                        <motion.div style={{ y, rotate }} className="hidden lg:block relative aspect-square max-w-sm">
                            <div className="absolute inset-8 rounded-full border border-[#FFB86B]/30 bg-[#FF6B35]/5 blur-sm" />
                            <img
                                src={MEDIA.trophy}
                                alt="JAF Award Trophy"
                                className="relative w-full h-full object-contain drop-shadow-[0_30px_60px_rgba(255,107,53,0.35)]"
                            />
                        </motion.div>
                    </div>

                    <div className="col-span-12 lg:col-span-7">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                            {AWARDS.map((a, i) => {
                                const Icon = AWARD_ICONS[i] || Award;
                                return (
                                    <motion.div
                                        key={a.title}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: "-30px" }}
                                        transition={{ delay: i * 0.05 }}
                                        whileHover={{ y: -4 }}
                                        className="group relative min-h-[150px] overflow-hidden rounded-2xl border border-[#D8A05D]/20 bg-[#111]/85 p-5 shadow-[0_16px_45px_rgba(0,0,0,0.28)] backdrop-blur transition hover:border-[#FFB86B]/70 hover:bg-[#17110C]/95 md:p-6"
                                        data-testid={`award-${i}`}
                                    >
                                        <div className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-[#FFB86B]/70 to-transparent opacity-0 transition group-hover:opacity-100" />
                                        <div className="relative flex items-start gap-4">
                                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#D8A05D]/40 bg-[#FFB86B]/8 text-[#F6C982] transition group-hover:bg-[#FF6B35] group-hover:text-black">
                                                <Icon size={22} strokeWidth={1.8} />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#FF9A5D] mb-2">
                                                    0{(i + 1).toString().padStart(2, "0")}
                                                </div>
                                                <h3 className="font-display text-base sm:text-lg md:text-xl font-bold leading-tight text-white [overflow-wrap:anywhere]">
                                                    {a.title}
                                                </h3>
                                                <p className="mt-2 text-sm leading-relaxed text-zinc-300 [overflow-wrap:anywhere]">{a.desc}</p>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export function LayoutSection() {
    return (
        <section
            id="layout"
            className="relative py-24 md:py-40 overflow-hidden"
            data-testid="layout-section"
        >
            <SectionBg src={PDF_BG.layout} opacity={0.6} />
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                <div className="grid grid-cols-12 gap-6 md:gap-12 mb-12 md:mb-16">
                    <div className="col-span-12 md:col-span-4 text-xs uppercase tracking-[0.3em] text-[#FF6B35] font-mono">
                        Venue Layout
                    </div>
                    <h2 className="col-span-12 md:col-span-8 min-w-0 max-w-full font-display font-bold text-[1.62rem] leading-[1.14] tracking-normal break-words [overflow-wrap:anywhere] sm:text-5xl sm:leading-[1.05] sm:tracking-tight lg:text-6xl">
                        A festival <span className="text-stroke-orange">city</span> within the city.
                    </h2>
                </div>

                <div className="relative rounded-3xl border border-white/10 bg-[#141414] p-5 sm:p-8 md:p-12 overflow-hidden">
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,107,53,0.15)_0%,transparent_50%),radial-gradient(circle_at_70%_50%,rgba(157,76,221,0.15)_0%,transparent_50%)]" />

                    <div className="relative grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            { label: "Main Stage" },
                            { label: "Awards Area" },
                            { label: "Media & Podcast Studio" },
                            { label: "Experience Zones" },
                            { label: "VIP Hospitality Lounge" },
                            { label: "Creator Zone" },
                            { label: "Exhibition Area" },
                            { label: "Networking Lounge" },
                            { label: "Registration Area" },
                            { label: "Brand Activation Areas" },
                            { label: "Food & Beverage Spaces" },
                            { label: "Performance Zones" },
                        ].map((z, i) => (
                            <motion.div
                                key={z.label}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.07 }}
                                className="group p-6 rounded-2xl glass-light hover:border-[#FF6B35]/60 transition"
                                data-testid={`layout-zone-${i}`}
                            >
                                <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF6B35] mb-3">
                                    Block 0{i + 1}
                                </div>
                                <div className="font-display text-lg sm:text-xl font-bold tracking-normal sm:tracking-tight [overflow-wrap:anywhere]">{z.label}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

export function Vision() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const opacity = useTransform(scrollYProgress, [0.1, 0.5, 0.9], [0.3, 1, 0.3]);
    const y = useTransform(scrollYProgress, [0, 1], [60, -50]);

    return (
        <section
            id="vision"
            ref={ref}
            className="relative py-24 md:py-40 overflow-hidden"
            data-testid="vision-section"
        >
            <SectionBg src={PDF_BG.vision} opacity={0.6} />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(255,107,53,0.18)_0%,_rgba(157,76,221,0.08)_30%,_transparent_70%)]" />

            <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                <motion.div style={{ opacity, y }} className="grid grid-cols-12 gap-6 md:gap-12 items-start max-[639px]:!transform-none">
                    <div className="col-span-12 lg:col-span-5 min-w-0">
                        <div className="text-xs uppercase tracking-[0.3em] text-[#FF6B35] font-mono mb-5">
                            {VISION.eyebrow}
                        </div>
                        <h2 className="min-w-0 max-w-full font-display font-black text-[1.62rem] leading-[1.14] tracking-normal break-words [overflow-wrap:anywhere] sm:text-5xl sm:leading-[1.05] sm:tracking-tighter lg:text-6xl mb-6">
                            {VISION.title}
                        </h2>
                        <p className="max-w-xl text-sm sm:text-base md:text-xl text-zinc-200 leading-relaxed [overflow-wrap:anywhere]">{VISION.body}</p>

                        <div className="mt-8 md:mt-10 rounded-3xl border border-[#D8A05D]/30 bg-[#080808]/70 p-5 sm:p-6 backdrop-blur">
                            <div className="text-4xl text-[#F6C982] leading-none mb-4">"</div>
                            <p className="font-mono uppercase tracking-[0.12em] sm:tracking-[0.18em] text-sm sm:text-lg md:text-2xl leading-relaxed text-white [overflow-wrap:anywhere]">
                                {VISION.quote}
                            </p>
                        </div>
                    </div>

                    <div className="col-span-12 lg:col-span-7">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                            {VISION.points.map((point, i) => {
                                const Icon = VISION_ICONS[i] || Star;
                                return (
                                    <motion.div
                                        key={point.title}
                                        initial={{ opacity: 0, y: 22 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: "-40px" }}
                                        transition={{ delay: i * 0.05 }}
                                        className="group rounded-2xl border border-[#D8A05D]/24 bg-[#101010]/82 p-5 backdrop-blur transition hover:border-[#FFB86B]/70 hover:bg-[#17110C]/95"
                                        data-testid={`vision-point-${i}`}
                                    >
                                        <div className="flex gap-4">
                                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#D8A05D]/45 text-[#F6C982] transition group-hover:bg-[#FF6B35] group-hover:text-black">
                                                <Icon size={20} strokeWidth={1.7} />
                                            </div>
                                            <div>
                                                <h3 className="text-sm font-bold uppercase tracking-wide text-[#F6C982] [overflow-wrap:anywhere]">
                                                    {point.title}
                                                </h3>
                                                <p className="mt-2 text-sm leading-relaxed text-zinc-300 [overflow-wrap:anywhere]">{point.desc}</p>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </motion.div>

                <div className="mt-12 rounded-3xl border border-[#D8A05D]/25 bg-[#080808]/78 p-5 backdrop-blur md:p-6">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto_1fr_auto_1fr] md:items-center">
                        {VISION.journey.map((item, i) => {
                            const Icon = JOURNEY_ICONS[i] || Globe;
                            const node = (
                                <div key={item.title} className="flex items-center gap-4" data-testid={`vision-journey-${i}`}>
                                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-[#D8A05D]/30 text-[#F6C982]">
                                        <Icon size={26} strokeWidth={1.6} />
                                    </div>
                                    <div>
                                        <div className="font-display text-xl sm:text-2xl font-bold uppercase tracking-wide text-[#F6C982] [overflow-wrap:anywhere]">
                                            {item.title}
                                        </div>
                                        <p className="mt-1 text-sm leading-relaxed text-zinc-300">{item.desc}</p>
                                    </div>
                                </div>
                            );

                            if (i === VISION.journey.length - 1) return node;
                            return [
                                node,
                                <ChevronsRight
                                    key={`arrow-${i}`}
                                    className="hidden text-[#FFB86B] md:block"
                                    size={34}
                                    strokeWidth={1.7}
                                />,
                            ];
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}
